/**
 * Angular `GenerateQuoteConstants.QuoteVesrion.MoreDetails.EnabledFields` column order parity
 * (`generate-quote.constant.ts`) for Quote Details modal grids.
 *
 * **Escalator column label (`termAprEscalator`):**
 * - **Good Leap** (Solar, Solar+Battery, Battery Only): show **Solar Escalator** — matches `Escalator` /
 *   lease–storage More Details (same binding as SB Good Leap screenshot).
 * - **Sunnova** (all products): keep **Terms & Apr / Escalator** — Angular uses that heading for Sunnova rows.
 */

export interface QuoteField {
    key: string;
    label: string;
    editable: boolean;
    type: 'text' | 'select' | 'toggle' | 'rating' | 'proposalEye' | 'combinedRate';
    options?: string[];
    proposalTarget?: 'battery' | 'roof' | 'solar';
}

export function normPartnerKey(s: string): string {
    return String(s || '').replace(/\s/g, '').toLowerCase();
}

/** Maps API `FinancePartner` to Angular `EnabledFields` finance key. */
export type AngularFinancePartnerKey =
    | 'lightreach'
    | 'sunlight'
    | 'goodleap'
    | 'sunnova'
    | 'cash'
    | 'servicefinance'
    | 'igs'
    | 'enfin'
    | 'default';

export function resolveAngularSolarFinanceKey(fp: unknown): AngularFinancePartnerKey {
    const k = normPartnerKey(String(fp ?? ''));
    if (k === 'lightreach') return 'lightreach';
    if (k === 'sunlight') return 'sunlight';
    if (k.includes('good') && k.includes('leap')) return 'goodleap';
    if (k === 'sunnova') return 'sunnova';
    if (k === 'cash') return 'cash';
    if (k.includes('service') && k.includes('finance')) return 'servicefinance';
    if (k === 'igs') return 'igs';
    if (k === 'enfin') return 'enfin';
    return 'default';
}

/**
 * React `QuoteField.key` order aligned with Angular `QuoteVesrion.MoreDetails.tableFilds`
 * (`generate-quote.constant.ts`) — same sequence as `field` / logical column order.
 */
const TABLE_FILDS_DISPLAY_ORDER: readonly string[] = [
    'quoteRating',
    'quoteNumber',
    'productDisplay',
    'financePartner',
    'financeMethod',
    'pricingMethodLine',
    'pricingMethod',
    'termAprEscalator',
    'TermAPR',
    'pricingValue',
    'combinedRate',
    'solarRate',
    'solarEscalator',
    'roofManufacturer',
    'roofProduct',
    'slopePrice',
    'ppwSolarOnly',
    'grossPpw',
    'rrSubtotal',
    'trinityEpcNet',
    'totalEpcWatt',
    'totalEpcGross',
    'totalEpcNet',
    'totalEpcLabel',
    'totalEpc',
    'monthlySunnovaPayment',
    'advancePaymentAmount',
    'annualProduction',
    'monthlyPayment',
    'MonthlyPayment',
    'energyCommunity',
    'domesticContent',
    'year1Savings',
    'termsAndApr',
    'termAndApr',
    'shingleColor',
    'normalRoofSquares',
    'lowRoofSquares',
    'dealerFeePct',
    'downPayment',
    'monthlyFinancePaymentNoItc',
    'itcAmount',
    'roofProposalEye',
    'roofProposal',
    'solarProposalEye',
    'solarProposal',
    'batteryProposalEye',
    'batteryProposal',
    'sunnovaSystemId',
    'PricingTier',
    'Battery_Price_Sub_Total',
    'Battery_Adder_Sub_Total',
    'rrSystemSize',
    'SunnovaQuote',
    'quoteExpiresIn',
    'Deposit_Amount',
    'depositAmount',
    'Financed_Amount',
    'financedAmount',
    'systemSize',
    'batteryManufacturer',
    'batteryModel',
    'batteryCount',
    'AmountOnPhysicalInstallStart',
    'AmountOnPhysicalInstallCompletion',
    'AmountOnContractCompletion',
    'AmountOnWorkCompletion',
    'plusAddersUponCompletion',
];

const TABLE_FILDS_ORDER_INDEX: ReadonlyMap<string, number> = new Map(
    TABLE_FILDS_DISPLAY_ORDER.map((key, index) => [key, index]),
);

/** Sort visible quote-detail columns to match Angular `tableFilds` order; unknown keys keep stable order after. */
export function sortQuoteFieldsByTableFildsOrder(fields: QuoteField[]): QuoteField[] {
    const fallback = TABLE_FILDS_DISPLAY_ORDER.length;
    return [...fields].sort((a, b) => {
        const ia = TABLE_FILDS_ORDER_INDEX.get(a.key) ?? fallback;
        const ib = TABLE_FILDS_ORDER_INDEX.get(b.key) ?? fallback;
        return ia - ib;
    });
}

function isSolarBatteryModalProductUi(productUi: string): boolean {
    return (
        productUi === 'Solar, Battery' ||
        productUi === 'Solar, Battery, Roof' ||
        productUi === 'SB' ||
        productUi === 'SBR'
    );
}

/** If API omits `ProductType` but the agreements row opened the modal as Solar+Battery, still map SB fields. */
export function modalWantsSbLayout(productUi: string, data: Record<string, unknown>): boolean {
    if (!isSolarBatteryModalProductUi(productUi)) return false;
    return (
        data.QuoteId != null ||
        data.MonthlyPayment != null ||
        data.TotalEPC != null ||
        (data.BatteryManufacturerName != null && String(data.BatteryManufacturerName).trim() !== '') ||
        (data.BatteryManufacturer != null && String(data.BatteryManufacturer).trim() !== '')
    );
}

// --- Default Solar (legacy editor grid) ---

export const QUOTE_FIELDS: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: true, type: 'select', options: ['LightReach', 'GoodLeap', 'IGS', 'Cash'] },
    { key: 'pricingMethod', label: 'Pricing Method', editable: true, type: 'select', options: ['PPW - Solar Rate', 'Cash Purchase', 'Lease', 'PPA'] },
    { key: 'pricingValue', label: 'Pricing Value', editable: true, type: 'text' },
    { key: 'solarRate', label: 'Solar Rate', editable: true, type: 'text' },
    { key: 'solarEscalator', label: 'Solar Escalator', editable: true, type: 'text' },
    { key: 'grossPpw', label: 'Gross PPW', editable: true, type: 'text' },
    { key: 'totalEpcWatt', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'year1Savings', label: 'Year 1 Savings', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: true, type: 'text' },
    { key: 'solarProposal', label: 'Solar Proposal', editable: true, type: 'toggle' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'AmountOnPhysicalInstallStart', label: 'Upon Commencement of Physical Install', editable: false, type: 'text' },
    { key: 'AmountOnPhysicalInstallCompletion', label: 'Upon Completion of Physical Install', editable: false, type: 'text' },
    { key: 'AmountOnContractCompletion', label: 'Upon Completion of Contract', editable: false, type: 'text' },
    { key: 'AmountOnWorkCompletion', label: 'Upon Completion of Work', editable: false, type: 'text' },
];

// --- Solar × partner ---

export const QUOTE_FIELDS_SOLAR_LIGHTREACH: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'productDisplay', label: 'Product', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    /** Angular `EnabledFields.Solar.Lightreach`: **Terms & Apr / Escalator** (`TermAPR` / `Escalator` in `more-details-model`). */
    { key: 'termAprEscalator', label: 'Terms & Apr / Escalator', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'ppwSolarOnly', label: 'PPW (Solar Only)', editable: false, type: 'text' },
    { key: 'totalEpcWatt', label: 'Total EPC', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'solarProposalEye', label: 'Solar Proposal', editable: false, type: 'proposalEye', proposalTarget: 'solar' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_SOLAR_SUNLIGHT: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'solarRate', label: 'Solar Rate', editable: false, type: 'text' },
    { key: 'grossPpw', label: 'Gross PPW', editable: false, type: 'text' },
    { key: 'totalEpcWatt', label: 'Total EPC', editable: false, type: 'text' },
    { key: 'termsAndApr', label: 'Terms and APR', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'solarProposalEye', label: 'Solar Proposal', editable: false, type: 'proposalEye', proposalTarget: 'solar' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_SOLAR_GOOD_LEAP: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'termAprEscalator', label: 'Solar Escalator', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'solarRate', label: 'Solar Rate', editable: false, type: 'text' },
    { key: 'grossPpw', label: 'Gross PPW', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'year1Savings', label: 'Year 1 Savings', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_SOLAR_CASH: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'solarRate', label: 'Solar Rate', editable: false, type: 'text' },
    { key: 'grossPpw', label: 'Desired PPW', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'solarProposalEye', label: 'Solar Proposal', editable: false, type: 'proposalEye', proposalTarget: 'solar' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
    { key: 'Deposit_Amount', label: 'Deposit Amount', editable: false, type: 'text' },
    { key: 'Financed_Amount', label: 'Financed Amount', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    /** Angular `more-details-model`: Solar + Cash unhides these three milestone headings. */
    { key: 'AmountOnPhysicalInstallStart', label: 'Upon Commencement of Physical Install', editable: false, type: 'text' },
    { key: 'AmountOnPhysicalInstallCompletion', label: 'Upon Completion of Physical Install', editable: false, type: 'text' },
    { key: 'AmountOnContractCompletion', label: 'Upon Completion of Contract', editable: false, type: 'text' },
];

/** Service Finance — Angular does not apply Cash-only milestone unhide; keep grid without deposit/milestone rows. */
export const QUOTE_FIELDS_SOLAR_SERVICE_FINANCE: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'solarRate', label: 'Solar Rate', editable: false, type: 'text' },
    { key: 'grossPpw', label: 'Gross PPW', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'solarProposalEye', label: 'Solar Proposal', editable: false, type: 'proposalEye', proposalTarget: 'solar' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_SOLAR_IGS: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'productDisplay', label: 'Product', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'solarRate', label: 'Solar Rate', editable: false, type: 'text' },
    /** Angular `EnabledFields.Solar.IGS` — same heading as Sunnova SB / tableFilds (not “Solar Escalator” alone). */
    { key: 'termAprEscalator', label: 'Terms & Apr / Escalator', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'ppwSolarOnly', label: 'PPW (Solar Only)', editable: false, type: 'text' },
    { key: 'totalEpcWatt', label: 'Total EPC', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'energyCommunity', label: 'Energy Community', editable: false, type: 'text' },
    { key: 'domesticContent', label: 'Domestic Content', editable: false, type: 'text' },
    { key: 'solarProposalEye', label: 'Solar Proposal', editable: false, type: 'proposalEye', proposalTarget: 'solar' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
    { key: 'SunnovaQuote', label: 'System Id', editable: false, type: 'text' }
];

export const QUOTE_FIELDS_SOLAR_SUNNOVA: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'productDisplay', label: 'Product', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'solarRate', label: 'Solar Rate', editable: false, type: 'text' },
    { key: 'termAprEscalator', label: 'Terms & Apr / Escalator', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'ppwSolarOnly', label: 'PPW (Solar Only)', editable: false, type: 'text' },
    { key: 'trinityEpcNet', label: "Trinity's EPC(Net)", editable: false, type: 'text' },
    { key: 'totalEpcGross', label: 'Total EPC(Gross)', editable: false, type: 'text' },
    { key: 'monthlySunnovaPayment', label: 'Monthly Sunnova Payment', editable: false, type: 'text' },
    { key: 'advancePaymentAmount', label: 'Advance Payment Amount', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'year1Savings', label: 'Year 1 Savings', editable: false, type: 'text' },
    { key: 'sunnovaSystemId', label: 'Sunnova System Id', editable: false, type: 'text' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
    { key: 'solarProposalEye', label: 'Solar Proposal', editable: false, type: 'proposalEye', proposalTarget: 'solar' },
    { key: 'monthlyFinancePaymentNoItc', label: 'Monthly Finance Payment after 18 months (if no ITC payback)', editable: false, type: 'text' },
    { key: 'itcAmount', label: 'ITC Amount', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_SOLAR_ENFIN: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'productDisplay', label: 'Product', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'grossPpw', label: 'Gross PPW', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'solarRate', label: 'Solar Rate', editable: false, type: 'text' },
    { key: 'termAprEscalator', label: 'Solar Escalator', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'year1Savings', label: 'Year 1 Savings', editable: false, type: 'text' },
]
export function getSolarQuoteFieldsForPartner(fp: unknown): QuoteField[] {
    const k = resolveAngularSolarFinanceKey(fp);
    switch (k) {
        case 'lightreach':
            return QUOTE_FIELDS_SOLAR_LIGHTREACH;
        case 'sunlight':
            return QUOTE_FIELDS_SOLAR_SUNLIGHT;
        case 'goodleap':
            return QUOTE_FIELDS_SOLAR_GOOD_LEAP;
        case 'cash':
            return QUOTE_FIELDS_SOLAR_CASH;
        case 'servicefinance':
            return QUOTE_FIELDS_SOLAR_SERVICE_FINANCE;
        case 'igs':
            return QUOTE_FIELDS_SOLAR_IGS;
        case 'sunnova':
            return QUOTE_FIELDS_SOLAR_SUNNOVA;
        case 'enfin':
            return QUOTE_FIELDS_SOLAR_ENFIN;
        default:
            return QUOTE_FIELDS;
    }
}

// --- Solar + Battery × partner ---

export const QUOTE_FIELDS_SB_SUNNOVA: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'productDisplay', label: 'Product', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'solarRate', label: 'Solar Rate', editable: false, type: 'text' },
    { key: 'termAprEscalator', label: 'Terms & Apr / Escalator', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'ppwSolarOnly', label: 'PPW (Solar Only)', editable: false, type: 'text' },
    { key: 'trinityEpcNet', label: "Trinity's EPC(Net)", editable: false, type: 'text' },
    { key: 'Battery_Price_Sub_Total', label: 'Battery price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'Battery_Adder_Sub_Total', label: 'Adder Price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'batteryManufacturer', label: 'Battery Manufacturer', editable: false, type: 'text' },
    { key: 'batteryModel', label: 'Battery Model', editable: false, type: 'text' },
    { key: 'batteryCount', label: 'Battery Count', editable: false, type: 'text' },
    { key: 'totalEpcGross', label: 'Total EPC(Gross)', editable: false, type: 'text' },
    { key: 'monthlySunnovaPayment', label: 'Monthly Sunnova Payment', editable: false, type: 'text' },
    { key: 'advancePaymentAmount', label: 'Advance Payment Amount', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'year1Savings', label: 'Year 1 Savings', editable: false, type: 'text' },
    { key: 'solarProposalEye', label: 'Solar Proposal', editable: false, type: 'proposalEye', proposalTarget: 'solar' },
    { key: 'sunnovaSystemId', label: 'Sunnova System Id', editable: false, type: 'text' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_SB_CASH: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'solarRate', label: 'Solar Rate', editable: false, type: 'text' },
    { key: 'grossPpw', label: 'Desired PPW', editable: false, type: 'text' },
    { key: 'Battery_Price_Sub_Total', label: 'Battery price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'Battery_Adder_Sub_Total', label: 'Adder Price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'batteryManufacturer', label: 'Battery Manufacturer', editable: false, type: 'text' },
    { key: 'batteryModel', label: 'Battery Model', editable: false, type: 'text' },
    { key: 'batteryCount', label: 'Battery Count', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'solarProposalEye', label: 'Solar Proposal', editable: false, type: 'proposalEye', proposalTarget: 'solar' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
    { key: 'Deposit_Amount', label: 'Deposit Amount', editable: false, type: 'text' },
    { key: 'Financed_Amount', label: 'Financed Amount', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    /** Solar + Cash (includes Solar, Battery) — same three milestones as pure Solar Cash. */
    { key: 'AmountOnPhysicalInstallStart', label: 'Upon Commencement of Physical Install', editable: false, type: 'text' },
    { key: 'AmountOnPhysicalInstallCompletion', label: 'Upon Completion of Physical Install', editable: false, type: 'text' },
    { key: 'AmountOnContractCompletion', label: 'Upon Completion of Contract', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_SB_SERVICE_FINANCE: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'solarRate', label: 'Solar Rate', editable: false, type: 'text' },
    { key: 'grossPpw', label: 'Gross PPW', editable: false, type: 'text' },
    { key: 'Battery_Price_Sub_Total', label: 'Battery price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'Battery_Adder_Sub_Total', label: 'Adder Price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'batteryManufacturer', label: 'Battery Manufacturer', editable: false, type: 'text' },
    { key: 'batteryModel', label: 'Battery Model', editable: false, type: 'text' },
    { key: 'batteryCount', label: 'Battery Count', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'solarProposalEye', label: 'Solar Proposal', editable: false, type: 'proposalEye', proposalTarget: 'solar' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_SB_LIGHTREACH: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'grossPpw', label: 'Gross PPW', editable: false, type: 'text' },
    { key: 'Battery_Price_Sub_Total', label: 'Battery price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'Battery_Adder_Sub_Total', label: 'Adder Price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'batteryManufacturer', label: 'Battery Manufacturer', editable: false, type: 'text' },
    { key: 'batteryModel', label: 'Battery Model', editable: false, type: 'text' },
    { key: 'batteryCount', label: 'Battery Count', editable: false, type: 'text' },
    { key: 'totalEpcWatt', label: 'Total EPC', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_SB_GOOD_LEAP: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'productDisplay', label: 'Product', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    /** Angular `EnabledFields["Solar, Battery"].Good Leap`: **Terms & Apr / Escalator** (then Combined Rate, Gross PPW, …). */
    { key: 'termAprEscalator', label: 'Terms & Apr / Escalator', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'batteryManufacturer', label: 'Battery Manufacturer', editable: false, type: 'text' },
    { key: 'batteryModel', label: 'Battery Model', editable: false, type: 'text' },
    { key: 'batteryCount', label: 'Battery Count', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'combinedRate', label: 'Combined Rate', editable: false, type: 'combinedRate' },
    { key: 'grossPpw', label: 'Gross PPW', editable: false, type: 'text' },
    { key: 'Battery_Price_Sub_Total', label: 'Battery price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'Battery_Adder_Sub_Total', label: 'Adder Price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'totalEpcLabel', label: 'Total EPC', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'year1Savings', label: 'Year 1 Savings', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_SB_ENFIN: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'productDisplay', label: 'Product', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'batteryManufacturer', label: 'Battery Manufacturer', editable: false, type: 'text' },
    { key: 'batteryModel', label: 'Battery Model', editable: false, type: 'text' },
    { key: 'batteryCount', label: 'Battery Count', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'combinedRate', label: 'Combined Rate', editable: false, type: 'combinedRate' },
    { key: 'grossPpw', label: 'Gross PPW', editable: false, type: 'text' },
    { key: 'Battery_Price_Sub_Total', label: 'Battery price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'Battery_Adder_Sub_Total', label: 'Adder Price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'totalEpcLabel', label: 'Total EPC', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'year1Savings', label: 'Year 1 Savings', editable: false, type: 'text' },
];

/** Fallback when partner is IGS / unknown — closest to generic SB grid. */
export const QUOTE_FIELDS_SOLAR_BATTERY_GENERIC: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'productDisplay', label: 'Product', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'combinedRate', label: 'Combined Rate', editable: false, type: 'combinedRate' },
    { key: 'termAprEscalator', label: 'Solar Escalator', editable: false, type: 'text' },
    { key: 'grossPpw', label: 'Gross PPW', editable: false, type: 'text' },
    { key: 'totalEpcLabel', label: 'Total EPC', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'year1Savings', label: 'Year 1 Savings', editable: false, type: 'text' },
    { key: 'Battery_Price_Sub_Total', label: 'Battery price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'Battery_Adder_Sub_Total', label: 'Adder Price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'batteryManufacturer', label: 'Battery Manufacturer', editable: false, type: 'text' },
    { key: 'batteryModel', label: 'Battery Model', editable: false, type: 'text' },
    { key: 'batteryCount', label: 'Battery Count', editable: false, type: 'text' },
];

function getSolarBatteryQuoteFieldsForPartner(fp: unknown): QuoteField[] {
    const k = resolveAngularSolarFinanceKey(fp);
    switch (k) {
        case 'sunnova':
            return QUOTE_FIELDS_SB_SUNNOVA;
        case 'cash':
            return QUOTE_FIELDS_SB_CASH;
        case 'servicefinance':
            return QUOTE_FIELDS_SB_SERVICE_FINANCE;
        case 'lightreach':
            return QUOTE_FIELDS_SB_LIGHTREACH;
        case 'goodleap':
            return QUOTE_FIELDS_SB_GOOD_LEAP;
        case 'enfin':
            return QUOTE_FIELDS_SB_ENFIN;
        case 'sunlight':
        case 'igs':
        default:
            return QUOTE_FIELDS_SOLAR_BATTERY_GENERIC;
    }
}

// --- Solar + Roof (SR) × partner ---

export const QUOTE_FIELDS_SR_SUNNOVA: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'productDisplay', label: 'Product', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'solarRate', label: 'Solar Rate', editable: false, type: 'text' },
    { key: 'termAprEscalator', label: 'Terms & Apr / Escalator', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'ppwSolarOnly', label: 'PPW (Solar Only)', editable: false, type: 'text' },
    // { key: 'rrSubtotal', label: 'R&R Subtotal', editable: false, type: 'text' },
    { key: 'trinityEpcNet', label: "Trinity's EPC(Net)", editable: false, type: 'text' },
    { key: 'totalEpcGross', label: 'Total EPC(Gross)', editable: false, type: 'text' },
    { key: 'monthlySunnovaPayment', label: 'Monthly Sunnova Payment', editable: false, type: 'text' },
    { key: 'advancePaymentAmount', label: 'Advance Payment Amount', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'year1Savings', label: 'Year 1 Savings', editable: false, type: 'text' },
    { key: 'roofProposalEye', label: 'Roof Proposal', editable: false, type: 'proposalEye', proposalTarget: 'roof' },
    { key: 'sunnovaSystemId', label: 'Sunnova System Id', editable: false, type: 'text' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_SR_CASH: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'solarRate', label: 'Solar Rate', editable: false, type: 'text' },
    { key: 'grossPpw', label: 'Desired PPW', editable: false, type: 'text' },
    { key: 'rrSubtotal', label: 'R&R Subtotal', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'solarProposalEye', label: 'Solar Proposal', editable: false, type: 'proposalEye', proposalTarget: 'solar' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
    { key: 'Deposit_Amount', label: 'Deposit Amount', editable: false, type: 'text' },
    { key: 'Financed_Amount', label: 'Financed Amount', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'AmountOnPhysicalInstallStart', label: 'Upon Commencement of Physical Install', editable: false, type: 'text' },
    { key: 'AmountOnPhysicalInstallCompletion', label: 'Upon Completion of Physical Install', editable: false, type: 'text' },
    { key: 'AmountOnContractCompletion', label: 'Upon Completion of Contract', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_SR_SERVICE_FINANCE: QuoteField[] = [
    { key: 'quoteRating', label: 'Quote Rating', editable: false, type: 'rating' },
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'solarRate', label: 'Solar Rate', editable: false, type: 'text' },
    { key: 'grossPpw', label: 'Gross PPW', editable: false, type: 'text' },
    { key: 'rrSubtotal', label: 'R&R Subtotal', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'annualProduction', label: 'Annual Production', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'systemSize', label: 'System Size', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'solarProposalEye', label: 'Solar Proposal', editable: false, type: 'proposalEye', proposalTarget: 'solar' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

function getSolarRoofQuoteFieldsForPartner(fp: unknown): QuoteField[] {
    const k = resolveAngularSolarFinanceKey(fp);
    switch (k) {
        case 'sunnova':
            return QUOTE_FIELDS_SR_SUNNOVA;
        case 'cash':
            return QUOTE_FIELDS_SR_CASH;
        case 'servicefinance':
            return QUOTE_FIELDS_SR_SERVICE_FINANCE;
        default:
            return QUOTE_FIELDS_SR_CASH;
    }
}

// --- Battery Only × partner ---

export const QUOTE_FIELDS_BO_SUNNOVA: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'productDisplay', label: 'Product', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethod', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'termAprEscalator', label: 'Terms & Apr / Escalator', editable: false, type: 'text' },
    { key: 'trinityEpcNet', label: "Trinity's EPC(Net)", editable: false, type: 'text' },
    { key: 'Battery_Price_Sub_Total', label: 'Battery price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'Battery_Adder_Sub_Total', label: 'Adder Price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'batteryManufacturer', label: 'Battery Manufacturer', editable: false, type: 'text' },
    { key: 'batteryModel', label: 'Battery Model', editable: false, type: 'text' },
    { key: 'batteryCount', label: 'Battery Count', editable: false, type: 'text' },
    { key: 'totalEpcGross', label: 'Total EPC(Gross)', editable: false, type: 'text' },
    { key: 'monthlySunnovaPayment', label: 'Monthly Sunnova Payment', editable: false, type: 'text' },
    { key: 'batteryProposalEye', label: 'Battery Proposal', editable: false, type: 'proposalEye', proposalTarget: 'battery' },
    { key: 'sunnovaSystemId', label: 'Sunnova System Id', editable: false, type: 'text' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_BO_CASH: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethod', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    /** Angular `EnabledFields["Battery Only"].Cash` + `more-details-model`: Cash forces `Solar Rate` visible ($0.000 typical). */
    { key: 'solarRate', label: 'Solar Rate', editable: false, type: 'text' },
    { key: 'PricingTier', label: 'Pricing Tier', editable: false, type: 'text' },
    { key: 'Battery_Price_Sub_Total', label: 'Battery price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'Battery_Adder_Sub_Total', label: 'Adder Price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'batteryManufacturer', label: 'Battery Manufacturer', editable: false, type: 'text' },
    { key: 'batteryModel', label: 'Battery Model', editable: false, type: 'text' },
    { key: 'batteryCount', label: 'Battery Count', editable: false, type: 'text' },
    { key: 'totalEpcWatt', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'MonthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'batteryProposalEye', label: 'Battery Proposal', editable: false, type: 'proposalEye', proposalTarget: 'battery' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
    { key: 'Deposit_Amount', label: 'Deposit Amount', editable: false, type: 'text' },
    { key: 'Financed_Amount', label: 'Financed Amount', editable: false, type: 'text' },
    /** Angular: Battery + Cash — Physical Install completion + Work completion (not the three Solar milestones). */
    { key: 'AmountOnPhysicalInstallCompletion', label: 'Upon Completion of Physical Install', editable: false, type: 'text' },
    { key: 'AmountOnWorkCompletion', label: 'Upon Completion of Work', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_BO_SERVICE_FINANCE: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethod', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'PricingTier', label: 'Pricing Tier', editable: false, type: 'text' },
    { key: 'Battery_Price_Sub_Total', label: 'Battery price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'Battery_Adder_Sub_Total', label: 'Adder Price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'batteryManufacturer', label: 'Battery Manufacturer', editable: false, type: 'text' },
    { key: 'batteryModel', label: 'Battery Model', editable: false, type: 'text' },
    { key: 'batteryCount', label: 'Battery Count', editable: false, type: 'text' },
    { key: 'totalEpcWatt', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'MonthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'batteryProposalEye', label: 'Battery Proposal', editable: false, type: 'proposalEye', proposalTarget: 'battery' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_BO_LIGHTREACH: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethod', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'PricingTier', label: 'Pricing Tier', editable: false, type: 'text' },
    { key: 'Battery_Price_Sub_Total', label: 'Battery price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'Battery_Adder_Sub_Total', label: 'Adder Price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'batteryManufacturer', label: 'Battery Manufacturer', editable: false, type: 'text' },
    { key: 'batteryModel', label: 'Battery Model', editable: false, type: 'text' },
    { key: 'batteryCount', label: 'Battery Count', editable: false, type: 'text' },
    { key: 'totalEpcWatt', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'MonthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'batteryProposalEye', label: 'Battery Proposal', editable: false, type: 'proposalEye', proposalTarget: 'battery' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_BO_GOOD_LEAP: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'productDisplay', label: 'Product', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethod', label: 'Pricing Method', editable: false, type: 'text' },
    /** Angular `EnabledFields["Battery Only"].Good Leap`: **Terms & Apr / Escalator**. */
    { key: 'TermAPR', label: 'Terms & Apr / Escalator', editable: false, type: 'text' },
    { key: 'batteryManufacturer', label: 'Battery Manufacturer', editable: false, type: 'text' },
    { key: 'batteryModel', label: 'Battery Model', editable: false, type: 'text' },
    { key: 'batteryCount', label: 'Battery Count', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'PricingTier', label: 'Pricing Tier', editable: false, type: 'text' },
    { key: 'Battery_Price_Sub_Total', label: 'Battery price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'Battery_Adder_Sub_Total', label: 'Adder Price Subtotal (Net)', editable: false, type: 'text' },
    { key: 'totalEpcLabel', label: 'Total EPC', editable: false, type: 'text' },
    { key: 'MonthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
];

function getBatteryOnlyQuoteFieldsForPartner(fp: unknown): QuoteField[] {
    const k = resolveAngularSolarFinanceKey(fp);
    switch (k) {
        case 'sunnova':
            return QUOTE_FIELDS_BO_SUNNOVA;
        case 'cash':
            return QUOTE_FIELDS_BO_CASH;
        case 'servicefinance':
            return QUOTE_FIELDS_BO_SERVICE_FINANCE;
        case 'lightreach':
            return QUOTE_FIELDS_BO_LIGHTREACH;
        case 'goodleap':
            return QUOTE_FIELDS_BO_GOOD_LEAP;
        case 'enfin':
            return QUOTE_FIELDS_BO_CASH;
        default:
            return QUOTE_FIELDS_BO_CASH;
    }
}

// --- Roof (R) × partner ---

export const QUOTE_FIELDS_ROOF_SUNNOVA: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'productDisplay', label: 'Product', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'termAprEscalator', label: 'Terms & Apr / Escalator', editable: false, type: 'text' },
    { key: 'rrSubtotal', label: 'R&R Subtotal', editable: false, type: 'text' },
    { key: 'trinityEpcNet', label: "Trinity's EPC(Net)", editable: false, type: 'text' },
    { key: 'totalEpcGross', label: 'Total EPC(Gross)', editable: false, type: 'text' },
    { key: 'monthlySunnovaPayment', label: 'Monthly Sunnova Payment', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'roofProposalEye', label: 'Roof Proposal', editable: false, type: 'proposalEye', proposalTarget: 'roof' },
    { key: 'sunnovaSystemId', label: 'Sunnova System Id', editable: false, type: 'text' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_ROOF_CASH: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'roofManufacturer', label: 'Roof Manufacturer', editable: false, type: 'text' },
    { key: 'roofProduct', label: 'Roof Product', editable: false, type: 'text' },
    { key: 'slopePrice', label: 'Slope Price', editable: false, type: 'text' },
    // { key: 'rrSubtotal', label: 'R&R Subtotal', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC (Net)', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'roofProposalEye', label: 'Roof Proposal', editable: false, type: 'proposalEye', proposalTarget: 'roof' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
    { key: 'depositAmount', label: 'Deposit Amount', editable: false, type: 'text' },
    { key: 'financedAmount', label: 'Financed Amount', editable: false, type: 'text' },
    /** Angular: Roof + Cash — `Plus Adders Upon Completion of Work` → `AmountOnWorkCompletion`. */
    { key: 'plusAddersUponCompletion', label: 'Plus Adders Upon Completion of Work', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_ROOF_SERVICE_FINANCE: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'roofManufacturer', label: 'Roof Manufacturer', editable: false, type: 'text' },
    { key: 'roofProduct', label: 'Roof Product', editable: false, type: 'text' },
    { key: 'slopePrice', label: 'Slope Price', editable: false, type: 'text' },
    // { key: 'rrSubtotal', label: 'R&R Subtotal', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC (Net)', editable: false, type: 'text' },
    { key: 'termsAndApr', label: 'Terms and APR', editable: false, type: 'text' },
    { key: 'dealerFeePct', label: 'Dealer Fee %', editable: false, type: 'text' },
    { key: 'totalEpcGross', label: 'Total EPC (Gross)', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'roofProposalEye', label: 'Roof Proposal', editable: false, type: 'proposalEye', proposalTarget: 'roof' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_ROOF_GOOD_LEAP: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'financeMethod', label: 'Finance Method', editable: false, type: 'text' },
    { key: 'termsAndApr', label: 'Terms and APR', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'shingleColor', label: 'Shingle Color', editable: false, type: 'text' },
    { key: 'normalRoofSquares', label: 'Normal Roof Squares', editable: false, type: 'text' },
    { key: 'lowRoofSquares', label: 'Low Roof Squares', editable: false, type: 'text' },
    { key: 'roofManufacturer', label: 'Roof Manufacturer', editable: false, type: 'text' },
    { key: 'roofProduct', label: 'Roof Product', editable: false, type: 'text' },
    { key: 'slopePrice', label: 'Slope Price', editable: false, type: 'text' },
    // { key: 'rrSubtotal', label: 'R&R Subtotal', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC (Net)', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'roofProposalEye', label: 'Roof Proposal', editable: false, type: 'proposalEye', proposalTarget: 'roof' },
];

export const QUOTE_FIELDS_ROOF_SUNLIGHT: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'roofManufacturer', label: 'Roof Manufacturer', editable: false, type: 'text' },
    { key: 'roofProduct', label: 'Roof Product', editable: false, type: 'text' },
    { key: 'slopePrice', label: 'Slope Price', editable: false, type: 'text' },
    // { key: 'rrSubtotal', label: 'R&R Subtotal', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC (Net)', editable: false, type: 'text' },
    { key: 'termsAndApr', label: 'Terms and APR', editable: false, type: 'text' },
    { key: 'dealerFeePct', label: 'Dealer Fee %', editable: false, type: 'text' },
    { key: 'totalEpcGross', label: 'Total EPC (Gross)', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'roofProposalEye', label: 'Roof Proposal', editable: false, type: 'proposalEye', proposalTarget: 'roof' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

/** Non-Cash roof financed fallback (legacy). */
export const QUOTE_FIELDS_ROOF_FINANCED_GENERIC: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'financeMethod', label: 'Finance Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'roofManufacturer', label: 'Roof Manufacturer', editable: false, type: 'text' },
    { key: 'roofProduct', label: 'Roof Product', editable: false, type: 'text' },
    { key: 'slopePrice', label: 'Slope Price', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'termAndApr', label: 'Terms and APR', editable: false, type: 'text' },
    { key: 'shingleColor', label: 'Shingle Color', editable: false, type: 'text' },
    { key: 'normalRoofSquares', label: 'Normal Roof Squares', editable: false, type: 'text' },
    { key: 'lowRoofSquares', label: 'Low Roof Squares', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'depositAmount', label: 'Deposit Amount', editable: false, type: 'text' },
    { key: 'financedAmount', label: 'Financed Amount', editable: false, type: 'text' },
];

function getRoofQuoteFieldsForPartner(fp: unknown, roofKind: 'roof' | 'roofOnly'): QuoteField[] {
    const k = resolveAngularSolarFinanceKey(fp);
    if (roofKind === 'roofOnly') {
        switch (k) {
            case 'sunnova':
                return QUOTE_FIELDS_ROOF_ONLY_SUNNOVA;
            case 'cash':
                return QUOTE_FIELDS_ROOF_ONLY_CASH;
            case 'servicefinance':
                return QUOTE_FIELDS_ROOF_ONLY_SERVICE_FINANCE;
            case 'goodleap':
                return QUOTE_FIELDS_ROOF_ONLY_GOOD_LEAP;
            case 'sunlight':
                return QUOTE_FIELDS_ROOF_ONLY_SUNLIGHT;
            default:
                return QUOTE_FIELDS_ROOF_FINANCED_GENERIC;
        }
    }
    switch (k) {
        case 'sunnova':
            return QUOTE_FIELDS_ROOF_SUNNOVA;
        case 'cash':
            return QUOTE_FIELDS_ROOF_CASH;
        case 'servicefinance':
            return QUOTE_FIELDS_ROOF_SERVICE_FINANCE;
        case 'goodleap':
            return QUOTE_FIELDS_ROOF_GOOD_LEAP;
        case 'sunlight':
            return QUOTE_FIELDS_ROOF_SUNLIGHT;
        default:
            return QUOTE_FIELDS_ROOF_FINANCED_GENERIC;
    }
}

// --- Roof Only (RO) × partner ---

export const QUOTE_FIELDS_ROOF_ONLY_SUNNOVA: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'productDisplay', label: 'Product', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'termAprEscalator', label: 'Terms & Apr / Escalator', editable: false, type: 'text' },
    // { key: 'rrSubtotal', label: 'R&R Subtotal', editable: false, type: 'text' },
    { key: 'trinityEpcNet', label: "Trinity's EPC(Net)", editable: false, type: 'text' },
    { key: 'totalEpcGross', label: 'Total EPC(Gross)', editable: false, type: 'text' },
    { key: 'monthlySunnovaPayment', label: 'Monthly Sunnova Payment', editable: false, type: 'text' },
    { key: 'roofProposalEye', label: 'Roof Proposal', editable: false, type: 'proposalEye', proposalTarget: 'roof' },
    { key: 'sunnovaSystemId', label: 'Sunnova System Id', editable: false, type: 'text' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_ROOF_ONLY_CASH: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'roofManufacturer', label: 'Roof Manufacturer', editable: false, type: 'text' },
    { key: 'roofProduct', label: 'Roof Product', editable: false, type: 'text' },
    { key: 'slopePrice', label: 'Slope Price', editable: false, type: 'text' },
    // { key: 'rrSubtotal', label: 'R&R Subtotal', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'roofProposalEye', label: 'Roof Proposal', editable: false, type: 'proposalEye', proposalTarget: 'roof' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
    { key: 'depositAmount', label: 'Deposit Amount', editable: false, type: 'text' },
    { key: 'financedAmount', label: 'Financed Amount', editable: false, type: 'text' },
    { key: 'plusAddersUponCompletion', label: 'Plus Adders Upon Completion of Work', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_ROOF_ONLY_SERVICE_FINANCE: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingMethodLine', label: 'Pricing Method', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'roofManufacturer', label: 'Roof Manufacturer', editable: false, type: 'text' },
    { key: 'roofProduct', label: 'Roof Product', editable: false, type: 'text' },
    { key: 'slopePrice', label: 'Slope Price', editable: false, type: 'text' },
    // { key: 'rrSubtotal', label: 'R&R Subtotal', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'termsAndApr', label: 'Terms and APR', editable: false, type: 'text' },
    { key: 'dealerFeePct', label: 'Dealer Fee %', editable: false, type: 'text' },
    { key: 'totalEpcGross', label: 'Total EPC(Gross)', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'roofProposalEye', label: 'Roof Proposal', editable: false, type: 'proposalEye', proposalTarget: 'roof' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_ROOF_ONLY_GOOD_LEAP: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'financeMethod', label: 'Finance Method', editable: false, type: 'text' },
    { key: 'termsAndApr', label: 'Terms and APR', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'shingleColor', label: 'Shingle Color', editable: false, type: 'text' },
    { key: 'normalRoofSquares', label: 'Normal Roof Squares', editable: false, type: 'text' },
    { key: 'lowRoofSquares', label: 'Low Roof Squares', editable: false, type: 'text' },
    { key: 'roofManufacturer', label: 'Roof Manufacturer', editable: false, type: 'text' },
    { key: 'roofProduct', label: 'Roof Product', editable: false, type: 'text' },
    { key: 'slopePrice', label: 'Slope Price', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'roofProposalEye', label: 'Roof Proposal', editable: false, type: 'proposalEye', proposalTarget: 'roof' },
    { key: 'depositAmount', label: 'Deposit Amount', editable: false, type: 'text' },
    { key: 'financedAmount', label: 'Financed Amount', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_ROOF_ONLY_SUNLIGHT: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'roofManufacturer', label: 'Roof Manufacturer', editable: false, type: 'text' },
    { key: 'roofProduct', label: 'Roof Product', editable: false, type: 'text' },
    { key: 'slopePrice', label: 'Slope Price', editable: false, type: 'text' },
    // { key: 'rrSubtotal', label: 'R&R Subtotal', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC (Net)', editable: false, type: 'text' },
    { key: 'termsAndApr', label: 'Terms and APR', editable: false, type: 'text' },
    { key: 'dealerFeePct', label: 'Dealer Fee %', editable: false, type: 'text' },
    { key: 'totalEpcGross', label: 'Total EPC (Gross)', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'roofProposalEye', label: 'Roof Proposal', editable: false, type: 'proposalEye', proposalTarget: 'roof' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

// --- RR ---

export const QUOTE_FIELDS_RR_CASH: QuoteField[] = [
    { key: 'quoteNumber', label: 'Quote', editable: false, type: 'text' },
    { key: 'financePartner', label: 'Finance Partner', editable: false, type: 'text' },
    { key: 'pricingValue', label: 'Pricing Value', editable: false, type: 'text' },
    { key: 'totalEpcNet', label: 'Total EPC(Net)', editable: false, type: 'text' },
    { key: 'rrSystemSize', label: 'R&R System Size', editable: false, type: 'text' },
    { key: 'grossPpw', label: 'Desired PPW', editable: false, type: 'text' },
    { key: 'downPayment', label: 'Down Payment', editable: false, type: 'text' },
    { key: 'monthlyPayment', label: 'Monthly Payment', editable: false, type: 'text' },
    { key: 'quoteExpiresIn', label: 'Quote Expires In', editable: false, type: 'text' },
];

export const QUOTE_FIELDS_RR_SERVICE_FINANCE = QUOTE_FIELDS_RR_CASH;

function getRrQuoteFieldsForPartner(fp: unknown): QuoteField[] {
    const k = resolveAngularSolarFinanceKey(fp);
    if (k === 'servicefinance') return QUOTE_FIELDS_RR_SERVICE_FINANCE;
    return QUOTE_FIELDS_RR_CASH;
}

// --- Unified resolver ---

export interface GetQuoteFieldsLayoutArgs {
    /** Modal `product` prop (e.g. agreements row label). */
    productUi: string;
    /** Resolved finance partner on the loaded quote row. */
    financePartner: unknown;
    /** Raw quote payload (`GetQuoteDetails` / `RetrieveContract` merged row). */
    data: Record<string, unknown>;
}

export function getQuoteFieldsLayout(args: GetQuoteFieldsLayoutArgs): QuoteField[] {
    const { productUi, financePartner, data } = args;
    const fp = financePartner;
    const pt = String(data.ProductType ?? data.productType ?? '').trim();
    const ptCompact = pt.replace(/\s/g, '');

    let fields: QuoteField[];

    if (pt === 'RR' || productUi === 'RR') {
        fields = getRrQuoteFieldsForPartner(fp);
    } else if (pt === 'SBR' || pt === 'Solar, Battery, Roof' || ptCompact === 'Solar,Battery,Roof') {
        /** Angular `EnabledFields["Solar, Battery, Roof"]` only lists Sunnova today; Cash/others follow SB partner grids. */
        fields = getSolarBatteryQuoteFieldsForPartner(fp);
    } else if (
        pt === 'SB' ||
        pt === 'Solar, Battery' ||
        ptCompact === 'Solar,Battery' ||
        (isSolarBatteryModalProductUi(productUi) && modalWantsSbLayout(productUi, data))
    ) {
        fields = getSolarBatteryQuoteFieldsForPartner(fp);
    } else if (pt === 'SR' || pt === 'Solar, Roof' || ptCompact === 'Solar,Roof') {
        fields = getSolarRoofQuoteFieldsForPartner(fp);
    } else if (pt === 'Battery Only' || pt === 'BO' || pt === 'B' || pt === 'Battery') {
        fields = getBatteryOnlyQuoteFieldsForPartner(fp);
    } else if (pt === 'Roof Only' || pt === 'RO') {
        fields = getRoofQuoteFieldsForPartner(fp, 'roofOnly');
    } else if (pt === 'Roof' || pt === 'R') {
        fields = getRoofQuoteFieldsForPartner(fp, 'roof');
    } else if (pt === 'Solar' || pt === 'S' || productUi === 'Solar') {
        fields = getSolarQuoteFieldsForPartner(fp);
    } else if (
        productUi === 'Solar, Battery' ||
        productUi === 'Solar, Battery, Roof' ||
        productUi === 'SB' ||
        productUi === 'SBR'
    ) {
        fields = getSolarBatteryQuoteFieldsForPartner(fp);
    } else if (productUi === 'Solar, Roof' || productUi === 'SR') {
        fields = getSolarRoofQuoteFieldsForPartner(fp);
    } else if (productUi === 'Battery Only' || productUi === 'Battery' || productUi === 'BO') {
        fields = getBatteryOnlyQuoteFieldsForPartner(fp);
    } else if (productUi === 'Roof Only' || productUi === 'RO') {
        fields = getRoofQuoteFieldsForPartner(fp, 'roofOnly');
    } else if (productUi === 'Roof' || productUi === 'R') {
        fields = getRoofQuoteFieldsForPartner(fp, 'roof');
    } else {
        fields = QUOTE_FIELDS;
    }

    return sortQuoteFieldsByTableFildsOrder(fields);
}
